import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import alertService from '../../../services/alertService';

const PayrollAlertWidget = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAlerts();
    
    // Subscribe to real-time alerts
    const channel = alertService?.subscribeToAlerts((newAlert) => {
      if (
        newAlert?.alertType === 'nasfund_deadline' ||
        newAlert?.alertType === 'swt_filing_error' ||
        newAlert?.alertType === 'payroll_approval_status'
      ) {
        setAlerts(prev => [newAlert, ...prev]?.slice(0, 5));
      }
    });
    
    return () => {
      alertService?.unsubscribeFromAlerts(channel);
    };
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const data = await alertService?.getUnreadAlerts();
      const payrollAlerts = data?.filter(
        a =>
          a?.alertType === 'nasfund_deadline' ||
          a?.alertType === 'swt_filing_error' ||
          a?.alertType === 'payroll_approval_status'
      ) || [];
      setAlerts(payrollAlerts?.slice(0, 5));
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (alertId) => {
    try {
      await alertService?.markAsRead(alertId);
      setAlerts(prev => prev?.filter(a => a?.id !== alertId));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-error/10',
          text: 'text-error',
          icon: 'AlertTriangle'
        };
      case 'high':
        return {
          bg: 'bg-warning/10',
          text: 'text-warning',
          icon: 'AlertCircle'
        };
      case 'medium':
        return {
          bg: 'bg-accent/10',
          text: 'text-accent',
          icon: 'Info'
        };
      default:
        return {
          bg: 'bg-muted',
          text: 'text-muted-foreground',
          icon: 'Info'
        };
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader" size={32} color="var(--color-muted-foreground)" className="animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center py-8 text-error">
          <Icon name="AlertTriangle" size={32} color="var(--color-error)" className="mx-auto mb-2" />
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon name="Bell" size={20} color="var(--color-primary)" />
          <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground">
            Active Alerts
          </h3>
        </div>
        {alerts?.length > 0 && (
          <span className="px-2 py-1 text-xs font-caption bg-error/10 text-error rounded-full">
            {alerts?.length} active
          </span>
        )}
      </div>

      <div className="space-y-3">
        {alerts?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="CheckCircle" size={48} color="var(--color-success)" className="mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No active alerts</p>
          </div>
        ) : (
          alerts?.map((alert) => {
            const styles = getSeverityStyles(alert?.severity);
            return (
              <div
                key={alert?.id}
                className={`${styles?.bg} border border-current/20 rounded-lg p-3 transition-smooth`}
              >
                <div className="flex items-start gap-3">
                  <div className={styles?.text}>
                    <Icon name={styles?.icon} size={18} color="currentColor" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h4 className="font-body font-semibold text-sm text-foreground line-clamp-1">
                        {alert?.title}
                      </h4>
                      <button
                        onClick={() => handleMarkAsRead(alert?.id)}
                        className="text-xs text-muted-foreground hover:text-foreground transition-smooth"
                      >
                        <Icon name="X" size={14} color="currentColor" />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                      {alert?.message}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs caption text-muted-foreground">
                        {formatTimestamp(alert?.createdAt)}
                      </span>
                      {alert?.deadline && (
                        <span className="text-xs caption text-error">
                          Due: {new Date(alert?.deadline)?.toLocaleDateString('en-GB')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PayrollAlertWidget;